import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { apiService } from 'src/utils/apiServices';
import { PHYSICAL_API, withoutEmptyValues } from 'src/utils/physical-api';
import { redirect } from 'react-router';
import { toast } from 'react-toastify';

const PaymentContext = createContext();
const PaymentActionsContext = createContext();

export const PaymentProvider = ({ children }) => {
  const [payments, setPayments] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [take, setTake] = useState(25);

  const [invoices, setInvoices] = useState([]);
  const [invoicesTotal, setInvoiceTotal] = useState(0);
  const [invoicePage, setInvoicePage] = useState(1);
  const [invoiceTake, setInvoiceTake] = useState(25);

  const [paidGroups, setPaidGroups] = useState([]);
  const [paidGroupsTotal, setPaidGroupsTotal] = useState(0);
  const [paidGroupsPage, setPaidGroupsPage] = useState(1);
  const [paidGroupsTake, setPaidGroupsTake] = useState(25);
  const getPayments = useCallback(
    async (filters = { page, take }) => {
      try {
        const response = await apiService.get('/transactions', {
          params: filters,
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`,
          },
        });
        const data = await response.data;
        setPayments(data.payments);
        setTotal(data.total);
        setPage(data.page);
        setTake(data.take);
      } catch (error) {
        console.error('Error fetching Payments:', error);
        if (error.response && error.response.status === 401) {
          localStorage.removeItem('token');
          redirect('/');
        }
      }
    },
    [page, take]
  );

  const getPaymentsInvoices = useCallback(
    async (filters = { page, take }) => {
      try {
        const response = await apiService.get('/transactions/invoices/getAll', {
          params: filters,
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`,
          },
        });
        const data = await response.data;
        setInvoices(data.payments);
        setInvoiceTotal(data.total);
        setInvoicePage(data.page);
        setInvoiceTake(data.take);
      } catch (error) {
        console.error('Error fetching Payments:', error);
        if (error.response && error.response.status === 401) {
          localStorage.removeItem('token');
          redirect('/');
        }
      }
    },
    [page, take]
  );
  const getPaymentByID = useCallback(async (id) => {
    try {
      const response = await apiService.get(`/transactions/dashboard/get/${id}`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
      });
      const data = await response.data;
      return data;
    } catch (error) {
      console.error('Error fetching Payment by ID:', error);
      if (error.response && error.response.status === 401) {
        localStorage.removeItem('token');
        redirect('/');
      }
      return null; // Return null in case of an error
    }
  }, []);

  const refundPayment = useCallback(async (id, amount) => {
    try {
      const response = await apiService.post(
        `/transactions/refund/${id}`,
        { amount },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`,
          },
        }
      );

      const data = await response.data;
      toast.success('Payment refunded successfully');
      return data;
    } catch (error) {
      console.error('Error refunding payment:', error);

      const errorMessage =
        error.response?.data?.message || error.response?.data?.error || 'An error occurred';
      toast.error(errorMessage);

      if (error.response && error.response.status === 401) {
        localStorage.removeItem('token');
      }

      return null;
    }
  }, []);

  const invoiceStatusChange = useCallback(async (id, status) => {
    try {
      const response = await apiService.post(
        `/transactions/invoiceStatusChange/${id}`,
        { status },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`,
          },
        }
      );

      const data = await response.data;
      toast.success('Payment updated successfully');
      return data;
    } catch (error) {
      const errorMessage =
        error.response?.data?.message || error.response?.data?.error || 'An error occurred';
      toast.error(errorMessage);
      if (error.response && error.response.status === 401) {
        localStorage.removeItem('token');
      }
      return null;
    }
  }, []);

  const getPaidGroups = useCallback(
    async (filters = { page: paidGroupsPage, take: paidGroupsTake }) => {
      try {
        const response = await apiService.get(`${PHYSICAL_API.payments}/groups`, {
          params: withoutEmptyValues(filters),
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`,
          },
        });
        const data = await response.data;
        setPaidGroups(data.data || []);
        setPaidGroupsTotal(data.total || 0);
        setPaidGroupsPage(data.page || 1);
        setPaidGroupsTake(data.take || 25);
        return data;
      } catch (error) {
        console.error('Error fetching Paid Groups:', error);
        if (error.response && error.response.status === 401) {
          localStorage.removeItem('token');
          redirect('/');
        }
        return null;
      }
    },
    [paidGroupsPage, paidGroupsTake]
  );

  const initiateGroupPayment = useCallback(async ({ group_ids, currency, paymentMethod }) => {
    try {
      const response = await apiService.post(
        `${PHYSICAL_API.payments}/groups/initiate`,
        { group_ids, currency, paymentMethod },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`,
          },
        }
      );
      const data = await response.data;
      toast.success('Payment initiated successfully');
      return data;
    } catch (error) {
      const errorMessage =
        error.response?.data?.message || error.response?.data?.error || 'An error occurred';
      toast.error(errorMessage);
      if (error.response && error.response.status === 401) {
        localStorage.removeItem('token');
        redirect('/');
      }
      return null;
    }
  }, []);

  const cancelTerminalPaymentIntent = useCallback(
    async ({ paymentIntentId, readerId }) => {
      try {
        const response = await apiService.post(
          `${PHYSICAL_API.payments}/terminal/payment-intents/cancel`,
          { paymentIntentId, readerId },
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem('token')}`,
            },
          }
        );
        const data = await response.data;
        toast.success('Terminal payment canceled');
        return data;
      } catch (error) {
        const errorMessage =
          error.response?.data?.message || error.response?.data?.error || 'Failed to cancel';
        toast.error(errorMessage);
        if (error.response && error.response.status === 401) {
          localStorage.removeItem('token');
          redirect('/');
        }
        return null;
      }
    },
    []
  );

  const PaymentActionsValue = useMemo(
    () => ({
      getPayments,
      getPaymentByID,
      setPage,
      setTake,
      refundPayment,
      getPaymentsInvoices,
      invoiceStatusChange,
      initiateGroupPayment,
      getPaidGroups,
      setPaidGroupsPage,
      setPaidGroupsTake,
      cancelTerminalPaymentIntent,
    }),
    [
      getPayments,
      getPaymentsInvoices,
      getPaymentByID,
      refundPayment,
      invoiceStatusChange,
      initiateGroupPayment,
      getPaidGroups,
      cancelTerminalPaymentIntent,
    ]
  );

  const PaymentsValue = useMemo(
    () => ({ 
      payments, 
      total, 
      page, 
      take, 
      invoices, 
      invoicesTotal, 
      invoicePage, 
      invoiceTake,
      paidGroups,
      paidGroupsTotal,
      paidGroupsPage,
      paidGroupsTake
    }),
    [payments, total, page, take, invoices, invoicesTotal, invoicePage, invoiceTake, paidGroups, paidGroupsTotal, paidGroupsPage, paidGroupsTake]
  );

  return (
    <PaymentContext.Provider value={PaymentsValue}>
      <PaymentActionsContext.Provider value={PaymentActionsValue}>
        {children}
      </PaymentActionsContext.Provider>
    </PaymentContext.Provider>
  );
};

export const usePaymentData = () => useContext(PaymentContext);
export const usePaymentActions = () => useContext(PaymentActionsContext);

PaymentProvider.propTypes = {
  children: PropTypes.element,
};
